import React, { useState } from 'react'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { editedConfigAtom } from './atoms'
import { useHasChanges, useSetTopLevelValue } from './hooks'
import { Box, Button, MenuItem, Select, TextField } from '@mui/material'
import { NodeTypeSelect } from '../../components/tree/utility/NodeTypeSelect'
import {
  TopLevelChildContent,
  TopLevelChildContentNode,
  TopLevelChildNode,
} from './types'
import { ConfirmDialog } from 'shared/ui'
import { camelToTitle } from '../../components/Helpers'
import { RegexInput } from '../../components/utility/RegexInput'
import { ChangeableContent } from './utility'

export function NodeChildren({ configKey }: { configKey: string }) {
  const [editing, setEditing] = useState(false)
  const editedConfig = useAtomValue(editedConfigAtom)
  const [childNodes, setChildNodes] = useState(
    editedConfig?.[configKey].childNodes ?? [],
  )
  const setChildren = useSetTopLevelValue(configKey, 'childNodes')
  const { topLevelHasChanges } = useHasChanges()

  const [newListTitle, setNewListTitle] = useState('')
  const [newListType, setNewListType] = useState('')

  if (!editedConfig) return null

  const hasChanges = topLevelHasChanges(configKey, 'childNodes')

  if (!editing) {
    return (
      <Box>
        <ChangeableContent
          changed={hasChanges}
        >{`${childNodes.length ?? 0} Child Lists`}</ChangeableContent>
        <Button onClick={() => setEditing(true)}>Add</Button>
      </Box>
    )
  }

  const onDone = () => {
    setEditing(false)
    setChildren(() => childNodes)
  }

  const onAdd = () => {
    setChildNodes((prev) => [
      ...prev,
      { listTitle: newListTitle, childrenType: newListType, nodes: [] },
    ])
    setNewListType('')
    setNewListTitle('')
  }

  const updateChildAtIndex = (index: number) => {
    return (child: TopLevelChildNode) => {
      setChildNodes((prev) => {
        return [
          ...childNodes.slice(0, index),
          child,
          ...childNodes.slice(index + 1),
        ]
      })
    }
  }

  return (
    <Box>
      {childNodes.map((child, i) => (
        <EditTopLevelChildNode
          key={i}
          child={child}
          updateChild={updateChildAtIndex(i)}
          updateChildNodes={setChildNodes}
          configKey={configKey}
        />
      ))}
      <TextField
        label="List Title"
        value={newListTitle}
        onChange={({ target }) => setNewListTitle(target.value)}
      />
      <NodeTypeSelect type={newListType} onSelect={(t) => setNewListType(t)} />
      <Button disabled={!newListTitle || !newListType} onClick={onAdd}>
        Add
      </Button>
      <Button onClick={onDone}>Done</Button>
    </Box>
  )
}

function AttrTypeSelect({
  configKey,
  onSelect,
}: {
  configKey: string
  onSelect: (k: string) => void
}) {
  const editedConfig = useAtomValue(editedConfigAtom)
  const filteredAttrs = Object.entries(
    editedConfig?.[configKey]?.attributes ?? {},
  ).filter(([, { isMetadata }]) => {
    return !isMetadata
  })
  const attrKeys = filteredAttrs.map(([key]) => key)
  if (!attrKeys.length) return null

  return (
    <Select<string> onChange={(e) => onSelect(e.target.value)}>
      {attrKeys.map((type) => (
        <MenuItem key={type} value={type}>
          {type}
        </MenuItem>
      ))}
    </Select>
  )
}

function AttributeEditDialog({
  attr,
  onDone,
  onRemove,
}: {
  attr: TopLevelChildContentNode
  onRemove: () => void
  onDone: (c?: TopLevelChildContentNode) => void
}) {
  const { title, id } = attr
  const currentTitle = title ?? camelToTitle(id)
  const [newTitle, setNewTitle] = useState(currentTitle)
  const onClose = () => {
    onDone()
  }
  const onConfirm = () => {
    onDone({ title: newTitle, id })
  }
  return (
    <ConfirmDialog
      title={`${id}: ${currentTitle}`}
      onClose={onClose}
      onConfirm={onConfirm}
    >
      <TextField
        value={newTitle}
        onChange={({ target }) => setNewTitle(target.value)}
      />
      <Button onClick={onRemove}>Remove</Button>
    </ConfirmDialog>
  )
}

function EditTopLevelChildNode({
  child,
  updateChild,
  configKey,
  updateChildNodes,
}: {
  child: TopLevelChildNode
  updateChild: (c: TopLevelChildNode) => void
  configKey: string
  updateChildNodes: (v: (p: TopLevelChildNode[]) => TopLevelChildNode[]) => void
}) {
  const onRemoveChild = () => {
    updateChildNodes((prev) =>
      prev.filter(
        (c) =>
          c.listTitle !== child.listTitle ||
          c.childrenType !== child.childrenType,
      ),
    )
  }

  return (
    <Box padding="8px" border={'solid 1px'}>
      <Box key={child.listTitle}>
        <EditChildListTitle child={child} updateChild={updateChild} />:{' '}
        {child.childrenType}: {`${child.nodes.length} Children`}
        <AddChildList child={child} updateChild={updateChild} />
        <AddChildTitleFilter child={child} updateChild={updateChild} />
        <ListPlacement child={child} updateChild={updateChild} />
        <Button onClick={onRemoveChild}>Remove</Button>
      </Box>
      <TopLevelTitleFilter child={child} updateChild={updateChild} />
      <TopLevelChildContentNodes
        configKey={configKey}
        child={child}
        updateChild={updateChild}
      />
    </Box>
  )
}

function ListPlacement({
  child,
  updateChild,
}: {
  child: TopLevelChildNode
  updateChild: (c: TopLevelChildNode) => void
}) {
  const onPlacementUpdate = (placement: number) => {
    updateChild({
      ...child,
      placement,
    })
  }

  return (
    <TextField
      label="Placement"
      value={child.placement}
      type="number"
      onChange={({ target }) => onPlacementUpdate(Number(target.value))}
    />
  )
}

function TopLevelChildContentNodes({
  configKey,
  child,
  updateChild,
}: {
  configKey: string
  child: TopLevelChildNode
  updateChild: (c: TopLevelChildNode) => void
}) {
  const onRemoveChildsChild = (index: number) => {
    return () => {
      const newChildList = [
        ...child.nodes.slice(0, index),
        ...child.nodes.slice(index + 1),
      ]
      updateChild({
        ...child,
        nodes: newChildList,
      })
    }
  }

  const onUpdateChildsChild = (index: number) => {
    return (childsChild: TopLevelChildContent) => {
      const newChildList = [
        ...child.nodes.slice(0, index),
        childsChild,
        ...child.nodes.slice(index + 1),
      ]
      updateChild({
        ...child,
        nodes: newChildList,
      })
    }
  }

  return (
    <Box padding="10px">
      {child.nodes.map((cn, i) => {
        return (
          <EditTopLevelChildContent
            key={i}
            configKey={configKey}
            child={cn}
            onRemove={onRemoveChildsChild(i)}
            doUpdate={onUpdateChildsChild(i)}
          />
        )
      })}
    </Box>
  )
}

function EditChildListTitle({
  child,
  updateChild,
}: {
  child: TopLevelChildNode
  updateChild: (c: TopLevelChildNode) => void
}) {
  const [title, setTitle] = useState(child.listTitle)
  const [editing, setEditing] = useState(false)

  if (!editing) {
    return <Button onClick={() => setEditing(true)}>{title}</Button>
  }

  const onDone = () => {
    setEditing(false)
    updateChild({ ...child, listTitle: title })
  }

  return (
    <TextField
      value={title}
      onChange={({ target }) => setTitle(target.value)}
      onBlur={onDone}
    />
  )
}

function EditTopLevelChildContent({
  configKey,
  child,
  doUpdate,
  onRemove,
}: {
  configKey: string
  child: TopLevelChildContent
  doUpdate: (c: TopLevelChildContent) => void
  onRemove: () => void
}) {
  const [editingAttrIdx, setEditingAttrIdx] = useState<number>()
  const { attrs, ...rest } = child
  const onAddAttr = (a: string) => {
    doUpdate({
      ...rest,
      attrs: [...attrs, { id: a }],
    })
  }
  const onRemoveAttr = (idx: number) => {
    return () => {
      setEditingAttrIdx(undefined)
      doUpdate({
        ...rest,
        attrs: [...attrs.slice(0, idx), ...attrs.slice(idx + 1)],
      })
    }
  }
  const onEditAttr = (idx: number) => {
    return (cn?: TopLevelChildContentNode) => {
      setEditingAttrIdx(undefined)
      if (!cn) return
      doUpdate({
        ...rest,
        attrs: [...attrs.slice(0, idx), cn, ...attrs.slice(idx + 1)],
      })
    }
  }
  return (
    <Box>
      <EditContentTitle child={child} doUpdate={doUpdate} />: [
      {attrs.map((attr, idx) => {
        return <Button onClick={() => setEditingAttrIdx(idx)}>{attr.id}</Button>
      })}
      ]
      <AttrTypeSelect configKey={configKey} onSelect={onAddAttr} />
      <Button onClick={onRemove}>Remove</Button>
      {editingAttrIdx !== undefined && (
        <AttributeEditDialog
          attr={attrs[editingAttrIdx]}
          onRemove={onRemoveAttr(editingAttrIdx)}
          onDone={onEditAttr(editingAttrIdx)}
        />
      )}
    </Box>
  )
}

function TopLevelTitleFilter({
  child,
  updateChild,
}: {
  child: TopLevelChildNode
  updateChild: (c: TopLevelChildNode) => void
}) {
  if (!child.filteredChildren?.length) return null

  const onRemove = (index: number) => {
    updateChild({
      ...child,
      filteredChildren: [
        ...child.filteredChildren!.slice(0, index),
        ...child.filteredChildren!.slice(index + 1),
      ],
    })
  }

  return (
    <Box padding="10px">
      {child.filteredChildren.map((cn, i) => {
        return (
          <Box key={i}>
            {cn.titleLike}
            <Button onClick={() => onRemove(i)}>X</Button>
          </Box>
        )
      })}
    </Box>
  )
}

function EditContentTitle({
  child,
  doUpdate,
}: {
  child: TopLevelChildContent
  doUpdate: (c: TopLevelChildContent) => void
}) {
  const [title, setTitle] = useState(child.title)
  const [editing, setEditing] = useState(false)

  if (!editing) {
    return <Button onClick={() => setEditing(true)}>{title}</Button>
  }

  const onDone = () => {
    setEditing(false)
    doUpdate({ ...child, title })
  }

  return (
    <TextField
      value={title}
      onChange={({ target }) => setTitle(target.value)}
      onBlur={onDone}
    />
  )
}

function AddChildList({
  child,
  updateChild,
}: {
  child: TopLevelChildNode
  updateChild: (c: TopLevelChildNode) => void
}) {
  const [addingChild, setAddingChild] = useState(false)
  const [newChildName, setNewChildName] = useState('')

  const onAddChild = () => {
    if (addingChild) {
      if (newChildName) {
        updateChild({
          ...child,
          nodes: [...child.nodes, { title: newChildName, attrs: [] }],
        })
      }
      setAddingChild(false)
      setNewChildName('')
    } else {
      setAddingChild(true)
    }
  }

  return (
    <>
      {addingChild && (
        <TextField
          value={newChildName}
          onChange={({ target }) => setNewChildName(target.value)}
        />
      )}
      <Button onClick={onAddChild}>Add Child</Button>
    </>
  )
}

function AddChildTitleFilter({
  child,
  updateChild,
}: {
  child: TopLevelChildNode
  updateChild: (c: TopLevelChildNode) => void
}) {
  const [caseSensitive, setCaseSensitive] = useState(false)
  const [asRegex, setAsRegex] = useState<RegExp | string>('')
  const [addingFilter, setAddingFilter] = useState(false)

  const onAddFilter = () => {
    if (addingFilter) {
      updateChild({
        ...child,
        filteredChildren: [
          ...(child.filteredChildren ?? []),
          { titleLike: String(asRegex) },
        ],
      })
      setAddingFilter(false)
    } else {
      setAddingFilter(true)
    }
  }

  return (
    <>
      {addingFilter && (
        <RegexInput
          asRegex={asRegex}
          setAsRegex={setAsRegex}
          caseSensitive={caseSensitive}
          setCaseSensitive={setCaseSensitive}
          useGlobal={false}
        />
      )}
      <Button onClick={onAddFilter}>Add Title Filter</Button>
    </>
  )
}
